import { useState } from 'react';
import { Mail, Clock, MapPin, Send, CheckCircle } from 'lucide-react';

const subjects = [
  'Dúvida sobre o produto',
  'Problema técnico',
  'Cancelamento / Reembolso',
  'Sugestão de funcionalidade',
  'Parceria comercial',
  'Imprensa',
  'Outro',
];

const channels = [
  {
    icon: Mail,
    title: 'E-mail',
    description: 'Respondemos em até 24h úteis',
    value: 'suporte@mybank.com.br',
    href: 'mailto:suporte@mybank.com.br',
    color: 'bg-primary-50 dark:bg-primary-900/20 text-primary-600',
  },
  {
    icon: Clock,
    title: 'Central de Ajuda',
    description: 'Artigos e tutoriais disponíveis 24h',
    value: 'Acessar artigos',
    href: '/help',
    color: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600',
  },
  {
    icon: MapPin,
    title: 'Endereço',
    description: 'São Paulo, SP — Brasil',
    value: 'Av. Brigadeiro Faria Lima, 1234',
    href: null,
    color: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600',
  },
];

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Simulate API call
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);
    setSent(true);
  };

  return (
    <div className="bg-white dark:bg-gray-950 py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Fale com a gente
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            Estamos aqui para ajudar. Seja qual for sua dúvida, nosso time responde com atenção e rapidez.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Left: channels */}
          <div className="lg:col-span-2 space-y-5">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Canais de atendimento</h2>
            {channels.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} className="flex gap-4 p-4 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${c.color}`}>
                    <Icon size={18} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white text-sm">{c.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{c.description}</p>
                    {c.href ? (
                      <a href={c.href} className="text-primary-600 text-sm font-medium hover:text-primary-700">{c.value}</a>
                    ) : (
                      <p className="text-gray-700 dark:text-gray-300 text-sm">{c.value}</p>
                    )}
                  </div>
                </div>
              );
            })}

            <div className="mt-8 p-5 rounded-2xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
              <p className="font-semibold text-gray-900 dark:text-white mb-2">Horário de atendimento</p>
              <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex justify-between">
                  <span>Segunda a Sexta</span>
                  <span className="font-medium">09h - 18h</span>
                </div>
                <div className="flex justify-between">
                  <span>Sábado</span>
                  <span className="font-medium">10h - 14h</span>
                </div>
                <div className="flex justify-between">
                  <span>Domingo</span>
                  <span className="text-gray-400">Fechado</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-3">
            {sent ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-6">
                  <CheckCircle size={32} className="text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Mensagem enviada!</h2>
                <p className="text-gray-500 dark:text-gray-400 max-w-sm">
                  Recebemos sua mensagem e retornaremos em até 24 horas úteis no e-mail <strong>{form.email}</strong>.
                </p>
                <button
                  onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
                  className="mt-8 px-6 py-3 text-primary-600 border border-primary-300 rounded-xl font-semibold hover:bg-primary-50 transition-colors"
                >
                  Enviar outra mensagem
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5 bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 p-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Enviar mensagem</h2>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nome completo *</label>
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="João da Silva"
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">E-mail *</label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="joao@email.com"
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Assunto *</label>
                  <select
                    required
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  >
                    <option value="">Selecione um assunto</option>
                    {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mensagem *</label>
                  <textarea
                    required
                    rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Descreva sua dúvida ou problema com o máximo de detalhes possível..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-4 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white rounded-xl font-semibold transition-colors"
                >
                  {loading ? (
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send size={18} />
                      Enviar mensagem
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  Ao enviar, você concorda com nossa{' '}
                  <a href="/privacy" className="text-primary-600 hover:underline">Política de Privacidade</a>.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
